import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/workflows';

const PaymentWorkflowSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  stepsCount: z.number().int().min(0),
  activePlansCount: z.number().int().min(0),
  isDefault: z.boolean(),
  updatedAt: z.iso.datetime().transform(dayjs),
});

const PaymentWorkflowSummariesSchema = z.array(PaymentWorkflowSummarySchema);

export type PaymentWorkflowSummary = z.infer<typeof PaymentWorkflowSummarySchema>;

export const PAYMENT_WORKFLOWS_QUERY_KEY = ['paymentWorkflowSummaries'];

export async function loadPaymentWorkflowsQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = PaymentWorkflowSummariesSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'paymentWorkflows');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadPaymentWorkflows(options: QueryOptions<PaymentWorkflowSummary[]> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<PaymentWorkflowSummary[], ApiError>({
    queryKey: PAYMENT_WORKFLOWS_QUERY_KEY,
    queryFn: () => loadPaymentWorkflowsQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<PaymentWorkflowSummary[], ApiError, 'paymentWorkflows'>(
    'paymentWorkflows',
    queryResult,
  );
}
